import { useState } from 'react'

import { useQuery } from 'react-query'
import { useLocation, useRouteMatch } from 'react-router-dom'

import {
  fetchFloors,
  fetchManageFloors,
  fetchPointImages,
  fetchPoints,
  fetchProject,
  fetchFloor,
  fetchManageFloor,
  fetchImage,
  fetchPointImage,
} from '../../../../api/buildingFetches'
import { listProjects } from '../../../../api/projects'
import { Point } from '../../../../api/types'
import { fetchViewpointFlatImages } from '../../../../api/viewpointFlatImages'
import { useBuildingContext } from '../../../../contexts/buildingContext'
import { useUserContext } from '../../../../contexts/userContext'
import { PATH_STRINGS } from '../../../../hooks/useGeneratedPaths'
import { createObjectURLSafe } from '../../../../utils/imageUtils'
import { sortImagesNewestFirst } from '../../../../utils/selectImagesForDateRange'
import { resolveTourUseIpp } from '../../image_viewer/components/ViewerPane/components/tourUseIpp'

/** Floor fetch must re-run when A/B asks for untrusted NexLoc/Progress ipp. */
function wantUntrustedTourMetrology(search: string): boolean {
  const params = new URLSearchParams(search)
  return (
    params.get('include_untrusted_tour_metrology') === '1' ||
    resolveTourUseIpp(search)
  )
}

const NEVER_EXCLUDE_REGION_IMAGES =
  process.env.REACT_APP_NEVER_EXCLUDE_REGION_IMAGES === '1'

const urlCreator = window.URL || window.webkitURL

export const useListProjectsQuery = (status?: string) => {
  const { state: userState } = useUserContext()

  return useQuery(['projects'], async () => {
    return await listProjects(userState.public_id, status)
  })
}

export const useListProjectFloorsQuery = (projectId: string) => {
  return useQuery(
    ['floors', projectId],
    async () => {
      return await fetchFloors(projectId)
    },
    { enabled: !!projectId },
  )
}

export const useFloorImages = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper data type
  onSuccess: (data: any) => void,
  activeOnly = false,
  isReviewed?: boolean,
  isApproved?: boolean,
  excludeRegionPictures?: boolean,
  includeInactiveFloors = false,
) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper floorImageMap type
  const [floorImageMap, setFloorImageMap] = useState<any>({})
  const buildingState = useBuildingContext().state
  const { search } = useLocation()
  const untrustedTourMetrology = wantUntrustedTourMetrology(search)

  return useQuery(
    [
      activeOnly,
      buildingState.projectId,
      buildingState.floorId,
      excludeRegionPictures,
      // fetchFloor reads tourUseIpp from the URL; key it so A/B gets a refetch.
      untrustedTourMetrology,
    ],
    async () => {
      const floor = includeInactiveFloors
        ? await fetchManageFloor(buildingState.projectId, buildingState.floorId)
        : await fetchFloor(buildingState.projectId, buildingState.floorId)

      const floorPointsPromise = fetchPoints(
        buildingState.projectId,
        floor.floor_code,
        activeOnly,
      )

      if (floorImageMap[floor.floor_code]) {
        floor.imageUrl = floorImageMap[floor.floor_code]
      } else {
        floor.img = await fetchImage(floor.latest_floor_plan.web_image_url)
        floor.imageUrl = createObjectURLSafe(floor.img) ?? undefined
        if (floor.imageUrl) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper prevState type
          setFloorImageMap((prevState: any) => {
            prevState[floor.floor_code] = floor.imageUrl
            return prevState
          })
        }
      }

      const floorPoints = await floorPointsPromise

      if (floor.is_non_360_image_floor) {
        const flatImages = await fetchViewpointFlatImages(
          buildingState.projectId,
          floor.floor_code,
        )
        floor.flatImages = flatImages
        floor.images = flatImages
        floor.points = floorPoints.map((point: Point) => {
          const pointImages = sortImagesNewestFirst(
            flatImages.filter(
              img => String(img.sub_point_id) === String(point.point_id),
            ),
          )
          const latestFlatImage = pointImages[0] ?? null
          return {
            ...point,
            flatImage: latestFlatImage,
            has_flat_image: pointImages.length > 0,
            images: pointImages,
          }
        })
        return floor
      }

      const floorImagesParams = {
        is_reviewed: isReviewed,
        is_approved: isApproved,
        exclude_region_images: NEVER_EXCLUDE_REGION_IMAGES
          ? false
          : excludeRegionPictures,
      }

      const floorImages = await fetchPointImages(
        buildingState.projectId,
        floor.floor_code,
        floorImagesParams,
      )
      floor.images = floorImages
      floor.points = floorPoints.map((point: Point) => {
        let pointImages = floorImages.filter(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper image type
          (image: any) => image.sub_point_id === point.point_id,
        )
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper image types
        pointImages = [...pointImages].sort((a: any, b: any) =>
          new Date(a.taken_on).getTime() < new Date(b.taken_on).getTime()
            ? 1
            : -1,
        )
        return {
          ...point,
          images: pointImages,
        }
      })
      return floor
    },
    {
      retry: 3,
      enabled: !!buildingState.floorId,
      onSuccess: data => onSuccess(data),
    },
  )
}

export const useBuildingQuery = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper data type
  onSuccess: (data: any) => void,
  options?: { includeInactiveFloors?: boolean },
) => {
  const buildingState = useBuildingContext().state
  const includeInactiveFloors = options?.includeInactiveFloors ?? false

  return useQuery(
    ['building', buildingState.projectId, includeInactiveFloors],
    async () => {
      const building = await fetchProject(buildingState.projectId)
      building.floors = includeInactiveFloors
        ? await fetchManageFloors(buildingState.projectId)
        : await fetchFloors(buildingState.projectId)
      return building
    },
    {
      enabled: !!buildingState.projectId,
      onSuccess: data => {
        onSuccess({ projectData: data })
      },
    },
  )
}

export const findClosestDate = (
  searchDate: Date,
  dateA: string | Date,
  dateB: string | Date,
) => {
  const distancea = Math.abs(searchDate.getTime() - new Date(dateA).getTime())
  const distanceb = Math.abs(searchDate.getTime() - new Date(dateB).getTime())
  return distancea - distanceb
}

export const useImagesQuery = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper dateRange type
  dateRange: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper currentImage type
  currentImage: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper data type
  onSuccess: (data: any) => void,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper data type
  onError: (data: any) => void,
) => {
  const buildingState = useBuildingContext().state
  const progressFloorMatch = useRouteMatch([
    PATH_STRINGS.progressFloor,
    PATH_STRINGS.progressFloorMulti3d,
  ])
  const safetyFloorMatch = useRouteMatch([
    PATH_STRINGS.safetyMetricDetailFloor,
    PATH_STRINGS.safetyMetricDetailFloorPoint,
  ])

  const filterDateRange = (point: Point) => {
    if (buildingState.dateRange) {
      return (
        new Date(point.taken_on) >= buildingState.dateRange.startDate &&
        new Date(point.taken_on) <= buildingState.dateRange.endDate
      )
    }

    return true
  }

  return useQuery(
    [
      buildingState.projectId,
      buildingState.floorData.floor_code,
      buildingState.pointId,
      buildingState.dateRange,
    ],
    async () => {
      // Find image for current point
      // filter & sort by clicked_on so we get the most recent one.
      const images = buildingState.floorData.images
      if (!images || !Array.isArray(images)) {
        return null
      }
      let found = images
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper image type
        .filter((image: any) => buildingState.pointId == image.sub_point_id)
        .sort(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper image types
          (a: any, b: any) =>
            new Date(b.taken_on).getTime() - new Date(a.taken_on).getTime(),
        )

      if (buildingState.dateRange) {
        found = found.filter(filterDateRange)
      }

      const dateParam = new URLSearchParams(window.location.search).get('date')

      if (dateParam) {
        const searchDate = new Date(String(dateParam))

        if (progressFloorMatch || safetyFloorMatch) {
          const searchDateMax = new Date(searchDate)
          searchDateMax.setHours(23, 59, 59, 999)

          const beforeSearchDate = found.filter(
            (point: Point) => new Date(point.taken_on) <= searchDateMax,
          )

          if (beforeSearchDate.length > 0) {
            found = beforeSearchDate
          }
        }

        found.sort((pointA: Point, pointB: Point) =>
          findClosestDate(searchDate, pointA.taken_on, pointB.taken_on),
        )
      }

      const selectedImage = found[0]

      if (!selectedImage) {
        return null
      }

      // TODO: image metadata from backend was supposed to contain dimensions already

      // Fetch image metadata from Nextera API
      const viewpointsImage = await fetchPointImage(
        buildingState.projectId,
        buildingState.floorData.floor_code,
        selectedImage.id,
      )

      // Download the actual image file
      const imageBlob = await fetchImage(viewpointsImage.processed_image_url)
      const imageObjectURL = createObjectURLSafe(imageBlob)
      if (!imageObjectURL) {
        return null
      }

      // Revoke previous blob URL if any
      if (currentImage?.image) {
        urlCreator.revokeObjectURL(currentImage.image)
      }

      // Single object URL: use for dimension load and for return value
      const image = new Image()
      image.src = imageObjectURL
      await new Promise(resolve => {
        image.onload = resolve
      })

      // Prefer single-image response for calibration (height, x/y) so TourManager gets correct blue-dot pitch
      const data = {
        ...selectedImage,
        height: viewpointsImage?.height ?? selectedImage?.height,
        true_x: viewpointsImage?.x ?? selectedImage?.true_x,
        true_y: viewpointsImage?.y ?? selectedImage?.true_y,
      }

      return {
        image: imageObjectURL,
        processed_image: viewpointsImage.processed_image_object,
        data,
        dimensions: {
          width: image.width,
          height: image.height,
        },
      }
    },
    {
      retry: 3,
      onSuccess: data => onSuccess(data),
      enabled:
        !!buildingState.pointId &&
        Object.entries(buildingState.floorData).length !== 0 &&
        !buildingState.floorData?.is_non_360_image_floor,
      onError: _err => onError(null),
    },
  )
}
